import * as React from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import SleekButton from "@/components/ui/NavButtons";
import { WealthScopeDashboardMock, PortfolioIQDashboardMock } from "./ModuleDashboardMocks";


/* ─── Icon components (inline SVG, no extra deps) ─────────── */
const Icon: React.FC<{ path: string; className?: string }> = ({ path, className = "" }) => (
  <svg className={`w-5 h-5 shrink-0 ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d={path} />
  </svg>
);

/* ─── Data ─────────────────────────────────────────────────── */
const WEALTHSCOPE_FEATURES = [
  {
    icon: "M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3",
    label: "Net Worth Tracking",
    desc: "Aggregates all assets (properties, cash, investments) and liabilities (mortgages, loans, credit) into a single live net worth number that updates as your accounts change.",
  },
  {
    icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    label: "Buying Power Calculator",
    desc: "Calculates how much capital you can deploy into your next deal based on available liquidity, equity, and debt-to-income ratio — updated in real time.",
  },
  {
    icon: "M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z",
    label: "Deal Readiness Score",
    desc: "A composite AI score (0–100) that tells you exactly when you're financially ready to pursue a new deal — factoring in net worth, DTI, liquidity, and credit utilization.",
  },
  {
    icon: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z",
    label: "Bank Account Sync via Plaid",
    desc: "Connect checking, savings, and investment accounts in under 2 minutes via Plaid. Transactions and balances sync automatically — or enter manually if you prefer.",
  },
  {
    icon: "M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z",
    label: "Asset & Liability Management",
    desc: "Track every asset class — real estate equity, stocks, retirement accounts — alongside mortgages, HELOCs, auto loans, and credit balances in one unified ledger.",
  },
  {
    icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
    label: "Debt-to-Income Ratio (DTI)",
    desc: "Calculates your front-end and back-end DTI ratios automatically — the same numbers lenders use to qualify you — so you know where you stand before applying.",
  },
  {
    icon: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9",
    label: "AI-Powered Financial Alerts",
    desc: "Get notified when your net worth crosses a threshold, your DTI increases, or your deal readiness score changes — so you act on the right information at the right time.",
  },
];

const PORTFOLIOIQ_FEATURES = [
  {
    icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
    label: "DSCR Analysis — Per Property & Portfolio",
    desc: "Calculates Debt Service Coverage Ratio at the individual property level and aggregated across your entire portfolio. Instantly see which properties are cash-flow positive and which are at risk.",
    badge: "Lender-critical",
  },
  {
    icon: "M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z",
    label: "Cap Rate Calculator",
    desc: "Tracks net operating income (NOI) and property value to calculate cap rate per property. Compare properties side-by-side to identify your strongest and weakest performers.",
  },
  {
    icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    label: "Cash-on-Cash Return",
    desc: "Tracks annual pre-tax cash flow divided by total cash invested for each property. Know exactly what percentage return your cash is generating year over year.",
  },
  {
    icon: "M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
    label: "Schedule E Report — One Click",
    desc: "Generates a complete IRS Schedule E-ready report for your CPA covering all rental income, expenses, depreciation, and mortgage interest per property. Export as PDF in one click.",
    badge: "CPA-ready",
  },
  {
    icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
    label: "DSCR Lender Report — One Click",
    desc: "Creates a formatted lender-ready DSCR report for loan applications — including property financials, rent rolls, and coverage ratios. No more scrambling before a meeting.",
    badge: "Lender-ready PDF",
  },
  {
    icon: "M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z",
    label: "Portfolio Performance Dashboard",
    desc: "See all properties in one view — DSCR, cap rate, cash-on-cash, monthly cash flow, and occupancy. Sort and filter to surface your best and worst performers instantly.",
  },
  {
    icon: "M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    label: "What-If Refinance Analysis",
    desc: "Model refinance scenarios interactively: adjust interest rate, loan term, or new loan amount and instantly see the impact on DSCR, monthly cash flow, and 5-year return projections.",
  },
  {
    icon: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9",
    label: "Underperformance Alerts",
    desc: "Set thresholds for DSCR, cap rate, or cash flow. Get an alert the moment a property drops below your target — so you can refinance, sell, or adjust rents before it's too late.",
  },
];

const TOGETHER_POINTS = [
  {
    icon: "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6",
    title: "Know when to move",
    desc: "WealthScope's Deal Readiness Score tells you when your finances are positioned for a new acquisition — before you start making offers.",
  },
  {
    icon: "M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z",
    title: "Walk into lender meetings ready",
    desc: "PortfolioIQ generates your DSCR lender report in one click. WealthScope shows your net worth and DTI. Everything a lender needs, in one platform.",
  },
  {
    icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
    title: "Tax season with zero scrambling",
    desc: "PortfolioIQ produces Schedule E data per property. Your CPA gets a clean PDF — not a spreadsheet dump — and you spend minutes, not days, preparing.",
  },
];

/* ─── Feature row (used inside each module section) ─────────── */
const FeatureRow: React.FC<{
  icon: string;
  label: string;
  desc: string;
  badge?: string;
  accentColor: string;
  bgColor: string;
}> = ({ icon, label, desc, badge, accentColor, bgColor }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -18 }}
    transition={{ duration: 0.35, ease: "easeOut" }}
    className="flex gap-4 p-4 rounded-xl border dark:border-white/10 border-blue-200/70 dark:bg-white/[0.03] bg-white/70"
  >
    <div className={`flex-shrink-0 w-10 h-10 rounded-lg ${bgColor} flex items-center justify-center mt-0.5`}>
      <Icon path={icon} className={accentColor} />
    </div>
    <div>
      <div className="flex items-center gap-2 mb-1">
        <h4 className="text-sm font-semibold dark:text-white text-gray-900">{label}</h4>
        {badge && (
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${accentColor} ${bgColor}`}>
            {badge}
          </span>
        )}
      </div>
      <p className="text-sm dark:text-slate-400 text-gray-600 leading-relaxed">{desc}</p>
    </div>
  </motion.div>
);

/* ─── Module section ─────────────────────────────────────────── */
const ModuleSection: React.FC<{
  id: string;
  badge: string;
  title: string;
  tagline: string;
  description: string;
  price: string;
  features: typeof WEALTHSCOPE_FEATURES;
  renderMock: () => React.ReactNode;
  accentColor: string;
  bgColor: string;
  borderColor: string;
  ctaHref: string;
  reverse?: boolean;
}> = ({ id, badge, title, tagline, description, price, features, renderMock, accentColor, bgColor, borderColor, ctaHref, reverse }) => {
  const [activeFeature, setActiveFeature] = React.useState(0);

  React.useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 2600);
    return () => window.clearInterval(timer);
  }, [features.length]);

  return (
  <section id={id} className="scroll-mt-24 px-4 py-14 sm:px-8 lg:px-16">
    <div className={`max-w-[1200px] mx-auto rounded-3xl border dark:border-slate-700 border-blue-200/80 dark:bg-[#07163F] bg-white p-6 sm:p-8 lg:p-10 flex flex-col ${reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-10 lg:gap-12 items-center lg:items-stretch`}>

      {/* Text side */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.35 }}
        className="flex-1 min-w-0 flex flex-col"
      >
        <div className={`self-start inline-flex px-3 py-1 text-xs font-bold tracking-widest uppercase rounded-full border ${bgColor} ${accentColor} mb-4`}>
          {badge}
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold dark:text-white text-gray-900 mb-2">{title}</h2>
        <p className={`text-base font-semibold ${accentColor} mb-3`}>{tagline}</p>
        <p className="text-base dark:text-slate-300 text-gray-600 leading-relaxed mb-2">{description}</p>
        <p className="text-sm dark:text-slate-500 text-gray-400 mb-8">
          Starting at <span className="font-bold dark:text-white text-gray-800">{price}/month</span> · 14-day free trial
        </p>

        {/* Feature ticker (one-by-one infinite) */}
        <div className="mb-8">
          <div className="mb-3 flex items-center gap-2">
            {features.map((f, idx) => (
              <button
                key={`feature-dot-${f.label}`}
                type="button"
                aria-label={`Focus feature ${idx + 1}`}
                onClick={() => setActiveFeature(idx)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  activeFeature === idx ? "w-6 bg-blue-500" : "w-2 bg-blue-200 dark:bg-blue-800/70"
                }`}
              />
            ))}
          </div>
          <div className="min-h-[170px]">
            <AnimatePresence mode="wait">
              <FeatureRow
                key={features[activeFeature].label}
                icon={features[activeFeature].icon}
                label={features[activeFeature].label}
                desc={features[activeFeature].desc}
                badge={(features[activeFeature] as { badge?: string }).badge}
                accentColor={accentColor}
                bgColor={bgColor}
              />
            </AnimatePresence>
          </div>
        </div>

        <a href={ctaHref} target="_self" rel="noopener noreferrer" className="mt-auto block w-fit">
          <SleekButton variant="primary">Try {title} Free</SleekButton>
        </a>
      </motion.div>

      {/* Visual side — real app screenshot */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.25 }}
        transition={{ duration: 0.4 }}
        className="group mx-auto flex-shrink-0 w-full max-w-[600px] self-center"
      >
        <div className={`h-[420px] flex flex-col rounded-2xl border ${borderColor} border-blue-400/40 dark:border-blue-500/35 dark:bg-vw-surface bg-white overflow-hidden shadow-2xl dark:shadow-blue-900/20 transition-all duration-300 group-hover:border-blue-500/80 group-hover:shadow-[0_0_0_3px_rgba(59,130,246,0.35),0_18px_45px_-20px_rgba(59,130,246,0.55)]`}>
          {/* Browser chrome */}
          <div className={`shrink-0 px-4 py-2.5 border-b ${borderColor} dark:bg-vw-surface bg-gray-50 flex items-center gap-2`}>
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/70" />
              <div className="w-2.5 h-2.5 rounded-full bg-green-400/70" />
            </div>
            <div className="flex-1 flex items-center justify-center">
              <span className="text-[10px] dark:text-slate-500 text-gray-400 font-medium">app.vawena.ai — {title}</span>
            </div>
          </div>
          <div className="flex-1 flex flex-col justify-center bg-slate-50">{renderMock()}</div>
        </div>
      </motion.div>
    </div>
  </section>
  );
};

/* ─── Main export ────────────────────────────────────────────── */
export const FeaturesSection: React.FC = () => (
  <div className="relative">
    {/* ── Section nav / overview ──────────────────────────── */}
    <div className="py-12 px-4 text-center">
      <div className="inline-flex px-3 py-1.5 text-xs font-bold tracking-widest dark:text-blue-400 text-blue-700 uppercase dark:bg-blue-600/15 bg-blue-100 rounded-full mb-4">
        Platform Modules
      </div>
      <h2 className="text-3xl sm:text-4xl font-extrabold dark:text-white text-gray-900 mb-3">
        Everything you need. Nothing you don't.
      </h2>
      <p className="text-base dark:text-slate-400 text-gray-600 max-w-2xl mx-auto mb-8">
        Two modules built specifically for individual real estate investors who want data-driven clarity — not another spreadsheet.
      </p>
      {/* Jump links */}
      <div className="mx-auto max-w-[760px] rounded-2xl border dark:border-white/[0.08] border-blue-200/80 dark:bg-[#07163F]/50 bg-white p-3 flex flex-wrap justify-center gap-3">
        <a href="#wealthscope" className="flex items-center gap-2 px-4 py-2 rounded-full border dark:border-blue-500/30 border-blue-300 dark:bg-blue-500/10 bg-blue-100 dark:text-blue-400 text-blue-700 text-sm font-semibold dark:hover:bg-blue-500/20 hover:bg-blue-200 transition-colors">
          <span className="w-2 h-2 bg-blue-500 rounded-full" />
          WealthScope
        </a>
        <a href="#portfolioiq" className="flex items-center gap-2 px-4 py-2 rounded-full border dark:border-emerald-500/30 border-emerald-300 dark:bg-emerald-500/10 bg-emerald-100 dark:text-emerald-400 text-emerald-700 text-sm font-semibold dark:hover:bg-emerald-500/20 hover:bg-emerald-200 transition-colors">
          <span className="w-2 h-2 bg-emerald-500 rounded-full" />
          PortfolioIQ
        </a>
        <Link to="/pricing" className="flex items-center gap-2 px-4 py-2 rounded-full border dark:border-white/10 border-gray-200 dark:text-slate-400 text-gray-600 text-sm font-semibold dark:hover:bg-white/5 hover:bg-gray-100 transition-colors">
          View pricing →
        </Link>
      </div>
    </div>

    {/* ── WealthScope ─────────────────────────────────────── */}
    <ModuleSection
      id="wealthscope"
      badge="WealthScope"
      title="WealthScope"
      tagline="Know exactly when you're ready to invest — before you make an offer."
      description="Most investors don't know their true financial position until they're already in a deal. WealthScope changes that. It aggregates your entire financial life — accounts, properties, debts, assets — and turns it into clear investment intelligence."
      price="$9.99"
      features={WEALTHSCOPE_FEATURES}
      renderMock={() => <WealthScopeDashboardMock />}
      accentColor="text-blue-400"
      bgColor="bg-blue-500/10"
      borderColor="border-blue-500/20"
      ctaHref="https://app.vawena.ai/signup"
    />

    {/* ── PortfolioIQ ─────────────────────────────────────── */}
    <ModuleSection
      id="portfolioiq"
      badge="PortfolioIQ"
      title="PortfolioIQ"
      tagline="Know exactly how every property is performing — and have the reports to prove it."
      description="Managing even two rental properties means tracking dozens of metrics. PortfolioIQ calculates DSCR, cap rate, and cash-on-cash return for every property automatically, then generates the reports your lender and CPA actually need."
      price="$29.99"
      features={PORTFOLIOIQ_FEATURES}
      renderMock={() => <PortfolioIQDashboardMock />}
      accentColor="text-emerald-400"
      bgColor="bg-emerald-500/10"
      borderColor="border-emerald-500/20"
      ctaHref="https://app.vawena.ai/signup"
    />

    {/* ── How they work together ───────────────────────────── */}
    <section className="py-16 px-4 sm:px-8">
      <div className="max-w-[1100px] mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex px-3 py-1.5 text-xs font-bold tracking-widest dark:text-indigo-400 text-indigo-700 uppercase dark:bg-indigo-500/10 bg-indigo-100 rounded-full mb-4">
            Better Together
          </div>
          <h2 className="text-3xl font-extrabold dark:text-white text-gray-900 mb-3">
            Two modules. One complete picture.
          </h2>
          <p className="text-base dark:text-slate-400 text-gray-600 max-w-xl mx-auto">
            WealthScope and PortfolioIQ share data to give you insights neither could provide alone.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {TOGETHER_POINTS.map(pt => (
            <div key={pt.title} className="relative dark:bg-vw-surface bg-white border dark:border-white/[0.07] border-blue-200/80 rounded-2xl p-6 shadow-sm hover:dark:bg-vw-elevated hover:border-blue-400/60 transition-colors duration-200">
              <div className="w-10 h-10 bg-indigo-500/15 rounded-xl flex items-center justify-center mb-4">
                <Icon path={pt.icon} className="text-indigo-400" />
              </div>
              <h3 className="text-base font-bold dark:text-white text-gray-900 mb-2">{pt.title}</h3>
              <p className="text-sm dark:text-slate-400 text-gray-600 leading-relaxed">{pt.desc}</p>
            </div>
          ))}
        </div>

        {/* Bundle CTA */}
        <div className="dark:bg-vw-highlight bg-blue-600 rounded-2xl p-8 text-center border dark:border-blue-700/30 border-blue-700">
          <h3 className="text-xl font-extrabold text-white mb-2">
            Get both modules for $34.99/month
          </h3>
          <p className="text-sm text-blue-100 mb-6">
            Save $5/month vs buying separately · Annual plan saves 17% more
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href="https://app.vawena.ai/signup" target="_self" rel="noopener noreferrer">
              <SleekButton variant="primary">Start Free Trial</SleekButton>
            </a>
            <Link to="/pricing">
              <SleekButton variant="light">Compare plans</SleekButton>
            </Link>
          </div>
        </div>
      </div>
    </section>
  </div>
);
